import type { RouterOutputs } from "@/client/api";
import { ISSUERS, type CreditCard } from "@/data/schemas";
import type { UserCreditCard } from "@/server/api/routers/schemas";
import {
  differenceInDays,
  differenceInMonths,
  differenceInYears,
} from "date-fns";

export type CardReason = {
  card: CreditCard;
  reasons: string[];
};

export const getIneligibleCards = async (
  userCards: RouterOutputs["cards"]["findAll"],
  creditCards: CreditCard[],
): Promise<CardReason[]> => {
  const getNumberOfCardsInLastNMonths = (
    months: number,
    cards: RouterOutputs["cards"]["findAll"],
  ): number => {
    return cards.reduce((count, userCard) => {
      const match = getMatch(userCard);
      if (!match) {
        console.warn(
          `Card ${userCard.issuer} ${userCard.name} not found in CREDIT_CARDS. Avoiding counting it for number of cards in the last N months.`,
        );
        return count;
      }
      const countsTowards524 =
        match.countsTowards524 ??
        (!match.isBusiness ||
          (match.issuer === ISSUERS.CAPITAL_ONE && match.isBusiness));
      const difference = differenceInMonths(
        Date.now(),
        userCard.applicationDate,
      );
      return count + (countsTowards524 && difference < months ? 1 : 0);
    }, 0);
  };

  const getMatch = (userCard: UserCreditCard): CreditCard | undefined => {
    const match = creditCards.find((c) => c.cardId === userCard.cardId);
    if (!match) return undefined;
    return match;
  };

  /**
   * @param cards All Chase cards the user has.
   */
  /* Amex's Bonvoy welcome offer is shared across all three Amex Bonvoy cards —
   * getting a bonus on any one blocks the bonus on the others for ~7 years. */
  const AMEX_BONVOY_CARD_IDS = new Set([
    "4d2115812f09d194440ef99b1a5210cd", // Marriott Bonvoy Brilliant
    "1129280627d633940e575deabdaa105f", // Marriott Bonvoy Business
    "653b14a655f4edc9bfc0332e9370e139", // Marriott Bonvoy Bevy
  ]);

  const amexEligibilityRules = (): CardReason[] => {
    const numberAmexCreditCards = userCards
      .filter((c) => c.issuer === ISSUERS.AMERICAN_EXPRESS)
      .reduce<number>((accumulator, card) => {
        // Must be from Amex and not be one of their charge cards
        return /^.*(Green|Gold|Platinum).*$/.test(card.name)
          ? accumulator
          : accumulator + 1;
      }, 0);

    const numberAmexCardsInLastThreeMonths = getNumberOfCardsInLastNMonths(
      3,
      userCards.filter((c) => c.issuer === ISSUERS.AMERICAN_EXPRESS),
    );

    return creditCards.reduce<CardReason[]>(
      (accumulator: CardReason[], card) => {
        if (card.issuer !== ISSUERS.AMERICAN_EXPRESS) return accumulator;
        const reasons: string[] = [];

        if (
          card.name === "Platinum" &&
          userCards.some(
            (card) =>
              card.issuer === ISSUERS.AMERICAN_EXPRESS &&
              card.name.includes("Platinum"),
          )
        ) {
          reasons.push(
            "Lifetime rule for platinum - vanilla, Schwab, or Morgan Stanley.",
          );
        }

        if (numberAmexCreditCards >= 5) {
          reasons.push(
            "Amex Card Limit: You can have at most 5 Amex Credit Cards (Excludes Charge Cards).",
          );
        }

        if (numberAmexCardsInLastThreeMonths >= 2) {
          reasons.push("You can only get two Amex card per 90 day interval.");
        }

        if (
          userCards.some(
            (c) =>
              c.issuer === ISSUERS.AMERICAN_EXPRESS &&
              (c.cardId === card.cardId ||
                (AMEX_BONVOY_CARD_IDS.has(card.cardId) &&
                  AMEX_BONVOY_CARD_IDS.has(c.cardId))) &&
              differenceInYears(Date.now(), c.applicationDate) < 7,
          )
        ) {
          reasons.push('Amex card bonuses are once per "lifetime" (~7yr).');
        }

        accumulator.push({ card, reasons });
        return accumulator;
      },
      [] satisfies CardReason[],
    );
  };

  const boaEligibilityRules = (): CardReason[] => {
    const numberBoaCardsLastTwoMonths = getNumberOfCardsInLastNMonths(
      2,
      userCards
        .filter((c) => c.issuer === ISSUERS.BANK_OF_AMERICA)
        .filter((c) => {
          const match = getMatch(c);
          if (!match) return false;
          return !match.isBusiness;
        }),
    );
    const numberBoaCardsLastTwelveMonths = getNumberOfCardsInLastNMonths(
      12,
      userCards
        .filter((c) => c.issuer === ISSUERS.BANK_OF_AMERICA)
        .filter((c) => {
          const match = getMatch(c);
          if (!match) return false;
          return !match.isBusiness && c.issuer === ISSUERS.BANK_OF_AMERICA;
        }),
    );
    const numberBoaCardsLastTwentyFourMonths = getNumberOfCardsInLastNMonths(
      24,
      userCards
        .filter((c) => c.issuer === ISSUERS.BANK_OF_AMERICA)
        .filter((c) => {
          const match = getMatch(c);
          if (!match) return false;
          return !match.isBusiness && c.issuer === ISSUERS.BANK_OF_AMERICA;
        }),
    );

    return creditCards.reduce<CardReason[]>(
      (accumulator: CardReason[], card) => {
        if (card.issuer !== ISSUERS.BANK_OF_AMERICA) return accumulator;
        const reasons: string[] = [];

        if (
          userCards.reduce((accumulator_, card) => {
            if (card.issuer === ISSUERS.BANK_OF_AMERICA)
              return accumulator_ + 1;
            return accumulator_;
          }, 0) >= 3
        ) {
          reasons.push(
            "BoA: More than three cards is considered excessive and can result in account closure.",
          );
        }

        if (!card.isBusiness && numberBoaCardsLastTwoMonths >= 2) {
          reasons.push(
            "You can only get two BoA cards per 60 day interval (Biz Cards Exempt).",
          );
        }

        if (!card.isBusiness && numberBoaCardsLastTwelveMonths >= 3) {
          reasons.push(
            "You can only get three BoA cards per 12 month interval (Biz Cards Exempt).",
          );
        }

        if (!card.isBusiness && numberBoaCardsLastTwentyFourMonths >= 4) {
          reasons.push(
            "You can only get four BoA cards per 24 month interval (Biz Cards Exempt).",
          );
        }

        if (
          card.name.includes("Alaska") &&
          userCards.some(
            (card) =>
              card.name.includes("Alaska") &&
              differenceInMonths(new Date(), card.applicationDate) < 24,
          )
        ) {
          reasons.push(
            "BoA: Alaska bonuses can only be received every 24 months.",
          );
        }

        if (
          card.name.includes("Premium Rewards") &&
          userCards.some(
            (card) =>
              card.name.includes("Premium Rewards") &&
              differenceInMonths(new Date(), card.applicationDate) < 24,
          )
        ) {
          reasons.push(
            "BoA: Premium Rewards bonus can only be received every 24 months.",
          );
        }

        accumulator.push({ card, reasons });
        return accumulator;
      },
      [] satisfies CardReason[],
    );
  };

  const barclaysEligibilityRules = (): CardReason[] => {
    const cardsInLastTwoYears = getNumberOfCardsInLastNMonths(24, userCards);
    return creditCards.reduce<CardReason[]>(
      (accumulator: CardReason[], card) => {
        if (card.issuer !== ISSUERS.BARCLAYS) return accumulator;
        const reasons: string[] = [];
        if (cardsInLastTwoYears >= 6) {
          reasons.push(
            "Barclays usually doesn't give out any cards at or above 6/24.",
          );
        }

        if (
          card.name === "JetBlue Premier" &&
          userCards.some(
            (card) =>
              card.issuer === ISSUERS.BARCLAYS && card.name === "JetBlue Plus",
          )
        ) {
          reasons.push(
            "The JetBlue Premier is unavailable if you've ever had the JetBlue Plus.",
          );
        }

        accumulator.push({ card, reasons });
        return accumulator;
      },
      [] satisfies CardReason[],
    );
  };

  const capitalOneEligibilityRules = (): CardReason[] => {
    return creditCards.reduce<CardReason[]>(
      (accumulator: CardReason[], card) => {
        if (card.issuer !== ISSUERS.CAPITAL_ONE) return accumulator;
        const reasons: string[] = [];

        if (
          card.name.toLowerCase().includes("venture") &&
          userCards.some((userCard) => {
            /* Venture and Venture X share the 48-month restriction — having
             * either blocks all other Venture-family cards. */
            return (
              userCard.issuer === ISSUERS.CAPITAL_ONE &&
              userCard.name.toLowerCase().includes("venture") &&
              differenceInYears(new Date(), userCard.applicationDate) < 4
            );
          })
        ) {
          reasons.push(
            "You can only get this Venture card once every 48 months.",
          );
        }

        if (
          userCards.some(
            (c) =>
              c.issuer === ISSUERS.CAPITAL_ONE &&
              differenceInMonths(new Date(), c.applicationDate) < 6,
          )
        ) {
          reasons.push(
            "You can only get one Capital One card per 6 month interval.",
          );
        }

        accumulator.push({ card, reasons });
        return accumulator;
      },
      [] satisfies CardReason[],
    );
  };

  const chaseEligibilityRules = (): CardReason[] => {
    const numberPersonalCardsLastTwoYears = getNumberOfCardsInLastNMonths(
      24,
      userCards,
    );
    const numberPersonalCardsFromChaseLastTwoMonths =
      getNumberOfCardsInLastNMonths(
        2,
        userCards
          .filter((c) => c.issuer === ISSUERS.CHASE)
          .filter((c) => {
            const match = getMatch(c);
            if (!match) return false;
            return !match.isBusiness;
          }),
      );
    const numberCardsFromChaseLastOneMonth = getNumberOfCardsInLastNMonths(
      1,
      userCards.filter((c) => c.issuer === ISSUERS.CHASE),
    );

    return creditCards.reduce<CardReason[]>(
      (accumulator: CardReason[], card) => {
        if (card.issuer !== ISSUERS.CHASE) return accumulator;
        const reasons: string[] = [];

        if (numberPersonalCardsLastTwoYears > 5) {
          reasons.push("You're at or above 5/24.");
        }

        if (numberPersonalCardsFromChaseLastTwoMonths > 2) {
          reasons.push("<= 2 Personal Cards in 30 Days.");
        }

        if (card.isBusiness && numberCardsFromChaseLastOneMonth > 1) {
          reasons.push(
            "No Biz Cards if you got another Chase card last 30 days.",
          );
        }

        if (
          card.name.toLowerCase().includes("sapphire") &&
          userCards.some(
            (c) =>
              c.name.toLowerCase().includes("sapphire") &&
              differenceInYears(Date.now(), c.applicationDate) < 4,
          )
        ) {
          reasons.push(
            "Sapphire Cards are only eligible for new applicants every 48 months from last bonus.",
          );
        }

        if (
          card.name.toLowerCase().includes("southwest") &&
          userCards.some(
            (c) =>
              c.name.toLowerCase().includes("southwest") &&
              differenceInMonths(Date.now(), c.applicationDate) < 24,
          )
        ) {
          reasons.push(
            "Southwest Cards have a shared 24 month cooldown from last bonus.",
          );
        }

        if (
          !card.name.toLowerCase().includes("ink") &&
          userCards
            .filter(
              (c) => c.issuer === ISSUERS.CHASE && c.cardId === card.cardId,
            )
            .some((c) => differenceInMonths(Date.now(), c.applicationDate) < 24)
        ) {
          reasons.push("Only one sign up bonus per card per 24 months.");
        }

        if (
          userCards
            .filter((c) => c.issuer === ISSUERS.CHASE)
            .some((c) => differenceInDays(Date.now(), c.applicationDate) === 0)
        ) {
          reasons.push("Only one Chase card per 24 hours.");
        }

        accumulator.push({ card, reasons });
        return accumulator;
      },
      [] satisfies CardReason[],
    );
  };

  const citiEligibilityRules = (): CardReason[] => {
    return creditCards.reduce<CardReason[]>(
      (accumulator: CardReason[], card) => {
        if (card.issuer !== ISSUERS.CITI) return accumulator;
        const reasons: string[] = [];

        if (
          /^.*(Preferred|Premier|Prestige).*$/.test(card.name) &&
          userCards.some(
            (c) =>
              c.issuer === ISSUERS.CITI &&
              /^.*(Preferred|Premier|Prestige).*$/.test(c.name) &&
              differenceInMonths(Date.now(), c.applicationDate) < 24,
          )
        ) {
          reasons.push(
            "You can only get one Citi card per product line every 24 months.",
          );
        }

        if (
          !card.isBusiness &&
          userCards.some((c) => {
            const iterMatch = getMatch(c);
            if (!iterMatch) return false;
            return (
              c.issuer === ISSUERS.CITI &&
              !iterMatch.isBusiness &&
              differenceInDays(Date.now(), c.applicationDate) < 8
            );
          })
        ) {
          reasons.push("You can only get one personal Citi card every 8 days.");
        }

        if (
          userCards
            .filter((c) => c.issuer === ISSUERS.CITI)
            .reduce((accumulator_, c) => {
              if (differenceInDays(Date.now(), c.applicationDate) < 65)
                return accumulator_ + 1;
              return accumulator_;
            }, 0) >= 2
        ) {
          reasons.push("You can only get 2 Citi cards in 65 days.");
        }

        if (
          card.isBusiness &&
          userCards
            .filter((c) => c.issuer === ISSUERS.CITI)
            .some(
              (c) =>
                getMatch(c)?.isBusiness &&
                differenceInDays(Date.now(), c.applicationDate) < 95,
            )
        ) {
          reasons.push("You can only get 1 Citi Business cards in 95 days.");
        }

        if (
          userCards
            .filter((c) => c.issuer === ISSUERS.CITI)
            .reduce((accumulator_, c) => {
              return differenceInMonths(Date.now(), c.applicationDate) < 6
                ? accumulator_ + 1
                : accumulator_;
            }, 0) >= 6
        ) {
          reasons.push(
            "Citi generally declines more than 6 applications in 6 months.",
          );
        }

        /* No personal bonus within last 48mo */
        if (
          card.name === "AAdvantage Platinum Select World Elite" &&
          userCards.some(
            (c) =>
              c.issuer === ISSUERS.CITI &&
              c.name === "AAdvantage Platinum Select World Elite" &&
              differenceInMonths(Date.now(), c.applicationDate) < 48,
          )
        ) {
          reasons.push(
            "You can only get 1 Citi Personal Platinum Select card in 48 months.",
          );
        }

        /* No bonus from either the business or personal version within 48mo */
        if (
          card.name === "AAdvantage Business World Elite" &&
          (userCards.some(
            (c) =>
              c.issuer === ISSUERS.CITI &&
              c.name === "AAdvantage Business World Elite" &&
              differenceInMonths(Date.now(), c.applicationDate) < 48,
          ) ||
            userCards.some(
              (c) =>
                c.issuer === ISSUERS.CITI &&
                c.name === "AAdvantage Platinum Select World Elite" &&
                differenceInMonths(Date.now(), c.applicationDate) < 48,
            ))
        ) {
          reasons.push(
            "Business World Elite requires no personal or business version bonus received within the last four years.",
          );
        }

        accumulator.push({ card, reasons });
        return accumulator;
      },
      [] satisfies CardReason[],
    );
  };

  const wellsFargoEligibilityRules = (): CardReason[] => {
    return creditCards.reduce<CardReason[]>(
      (accumulator: CardReason[], card) => {
        if (card.issuer !== ISSUERS.WELLS_FARGO) return accumulator;
        const reasons: string[] = [];

        if (
          userCards
            .filter((c) => c.issuer === ISSUERS.WELLS_FARGO)
            .some((c) => differenceInMonths(Date.now(), c.applicationDate) < 15)
        ) {
          reasons.push(
            "You can only get one Wells Fargo bonus every 15 months.",
          );
        }
        accumulator.push({ card, reasons });
        return accumulator;
      },
      [] satisfies CardReason[],
    );
  };

  const usBankEligibilityRules = (): CardReason[] => {
    return creditCards.reduce<CardReason[]>(
      (accumulator: CardReason[], card) => {
        if (card.issuer !== ISSUERS.US_BANK) return accumulator;
        const reasons: string[] = [];
        accumulator.push({ card, reasons });
        return accumulator;
      },
      [] satisfies CardReason[],
    );
  };

  return [
    ...amexEligibilityRules(),
    ...boaEligibilityRules(),
    ...barclaysEligibilityRules(),
    ...capitalOneEligibilityRules(),
    ...chaseEligibilityRules(),
    ...citiEligibilityRules(),
    ...wellsFargoEligibilityRules(),
    ...usBankEligibilityRules(),
  ];
};
